<!--
    The delivery menu's list of rack loads. Serves both modes.

    With a project selected it lists that project's loads. With "All projects"
    it lists every load of the company, grouped under the project name — the
    view a shipping coordinator wants and the per-project screen cannot give.
    `loadRackTraces("all")` already does this: the store sends `?project=null`,
    `js_to_py()` maps it to `None`, and `MyProject` falls back to every
    non-archived project of the company.

    The group headers are labels, not collapsible sections. A collapsed group is
    a hidden control, and this menu is used on a tablet.
-->
<template>
    <div>
        <v-progress-linear v-if="loading" indeterminate color="accent" />

        <v-list density="default" lines="three" class="pt-0">
            <template v-for="group in groups" :key="group.key">
                <v-list-subheader v-if="group.title" class="group-header">
                    {{ group.title }}
                </v-list-subheader>
                <ListItemRackLoad v-for="trace in group.traces" :key="trace.uuid" :trace="trace" />
            </template>

            <v-list-item v-if="!loading && !visible.length">
                <v-list-item-subtitle>{{ t("no_rack_loads_here") }}</v-list-item-subtitle>
            </v-list-item>
        </v-list>
    </div>
</template>

<script setup>
import { computed, onMounted, watch } from "vue";
import { usePitStoreBasics } from "@/stores/app_common";
import { useAppStore } from "@/stores/app";
import { useCompanyStore } from "@/stores/company";
import { useLogisticsStore } from "@/stores/logistics";
import { useSelectedStore } from "@/stores/selected";
import ListItemRackLoad from "./ListItemRackLoad.vue";
import {
    sortRackLoads,
    rackLoadMatchesFilter,
    groupRackLoadsByProject,
} from "@/components/delivery/delivery_composables";

const { t } = usePitStoreBasics();
const app_store = useAppStore();
const company_store = useCompanyStore();
const logistics_store = useLogisticsStore();
const selected_store = useSelectedStore();

// `loader_pr` pushes the objects name while it runs, so the list can say it
// is fetching without a flag of its own.
const loading = computed(() => app_store.loader_is_active("RackTraces"));

const project_uuid = computed(() => selected_store.selected_project?.uuid || "");
const company_wide = computed(() => project_uuid.value === "all");

const visible = computed(() => sortRackLoads(
    logistics_store.rack_traces.filter(
        trace => rackLoadMatchesFilter(trace, logistics_store.delivery_filter))));

const project_name = (uuid) =>
    company_store.projects.find(p => p.uuid === uuid)?.name || "—";

const groups = computed(() => {
    if (!company_wide.value) return [{ key: "all", title: "", traces: visible.value }];
    return groupRackLoadsByProject(visible.value).map(
        group => ({ ...group, title: project_name(group.project) }));
});

// The delivery menu owns this list: the planning screen is a different mode and
// may not have been opened at all. The rack setups come with it because the
// drawing is scaled against them.
const reload = () => {
    if (!project_uuid.value) return;
    logistics_store.loadRackTraces(project_uuid.value);
    if (!logistics_store.racks.length) logistics_store.loadRacks();
};

watch(project_uuid, reload);
onMounted(reload);
</script>

<style scoped>
.group-header {
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    background: rgba(var(--v-theme-on-surface), 0.04);
    min-height: 32px;
}
</style>
